import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", ratePercentage: "", taxType: "CGST_SGST" };

const TaxRates = () => {
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (rate) => {
    setEditingId(rate.id);
    setFormData({ name: rate.name || "", ratePercentage: String(rate.ratePercentage ?? ""), taxType: rate.taxType || "CGST_SGST" });
    setShowModal(true);
  };

  const remove = async (rate) => {
    if (!window.confirm(`Delete tax rate "${rate.name}"? Invoices already saved with it are not changed.`)) return;
    try {
      await api.del(`/api/v1/products/tax-rates/${rate.id}`);
      toast.success("Tax rate deleted");
      loadTaxRates();
    } catch (error) {
      toast.error(error.message || "Failed to delete tax rate");
    }
  };

  const loadTaxRates = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/products/tax-rates");
      setTaxRates(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load tax rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTaxRates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, ratePercentage: Number(formData.ratePercentage) };
      if (editingId) await api.put(`/api/v1/products/tax-rates/${editingId}`, payload);
      else await api.post("/api/v1/products/tax-rates", payload);
      toast.success(editingId ? "Tax rate updated" : "Tax rate created");
      setShowModal(false);
      setFormData(emptyForm);
      loadTaxRates();
    } catch (error) {
      toast.error(error.message || "Failed to save tax rate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading tax rates...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Tax Rate
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Rate %</th>
              <th>Type</th>
              <th className="sticky-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {taxRates.length === 0 ? (
              <tr>
                <td colSpan={5}>No tax rates found.</td>
              </tr>
            ) : (
              taxRates.map((rate) => (
                <tr key={rate.id}>
                  <td>{rate.id}</td>
                  <td>{rate.name}</td>
                  <td>{rate.ratePercentage}</td>
                  <td>{rate.taxType}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(rate)}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => remove(rate)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Tax Rate" : "Create Tax Rate"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Rate Percentage</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="ratePercentage"
                    value={formData.ratePercentage}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tax Type</label>
                  <select name="taxType" value={formData.taxType} onChange={handleChange}>
                    <option value="CGST_SGST">CGST + SGST</option>
                    <option value="CGST">CGST only</option>
                    <option value="SGST">SGST only</option>
                    <option value="IGST">IGST</option>
                    <option value="NONE">None</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TaxRates;
